import React, {useEffect, useMemo} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {useConfigurationContext} from '../configuration/useConfigurationContext'
import {useLoadBalanceAdjustment} from '../fields/loadBalance/useLoadBalanceAdjustment'
import {useLoadSpacingAdjustment} from '../fields/loadSpacing/useLoadSpacingAdjustment'
import {useAdjustMultipleTrucks} from '../fields/trucks/useAdjustMultipleTrucks'
import {FieldsForm} from '../fields/types'

import {buildResolver} from './buildResolver'

type OrderProcessFormProviderProps = React.PropsWithChildren & {
  country: string
  defaultValues: FieldsForm
  /** True when used in order-change flow; false for order-intake flow. */
  isOrderChangeProcess?: boolean
  /**
   * Optional reactive form values (react-hook-form `values`). When provided, they seed the current
   * field values while `defaultValues` stays the dirty-comparison baseline — used by the order change
   * flow to pre-fill an unconfirmed UE/CC order's rescheduled time and mark the field dirty. Omit it
   * (order intake / normal change orders) to keep the original defaultValues-only behavior.
   */
  values?: FieldsForm
  /**
   * When `true`, keeps the original `defaultValues` as the dirty baseline while `values` drives the
   * current values. Required together with `values` for the pre-fill-as-dirty behavior to work.
   */
  keepDefaultValues?: boolean
}

export const OrderProcessFormProvider = ({
  children,
  defaultValues,
  values,
  keepDefaultValues,
  isOrderChangeProcess = false
}: OrderProcessFormProviderProps) => {
  const configuration = useConfigurationContext()
  const resolver = useMemo(
    () => buildResolver({configuration, defaultValues, isOrderChangeProcess}),
    []
  )

  const orderProcessForm = useForm<FieldsForm>({
    defaultValues,
    // Only engage react-hook-form's reactive `values` API when a working override is supplied, so
    // order intake and normal change orders keep the exact prior defaultValues-only behavior.
    ...(values ? {values} : {}),
    resolver,
    reValidateMode: 'onChange',
    mode: 'onChange',
    criteriaMode: 'all',
    shouldUnregister: false,
    resetOptions: {
      // we are refreshing values after fetching options, but we need to keep values provided by user
      keepDirty: true,
      // Keep the original defaults as the dirty baseline so a pre-filled `values` reads as dirty.
      ...(values && keepDefaultValues ? {keepDefaultValues: true} : {})
    }
  })

  // validate on start
  useEffect(() => {
    orderProcessForm.trigger()
  }, [orderProcessForm, orderProcessForm.trigger])

  return (
    <FormProvider {...orderProcessForm}>
      {children}
      {/**
       * This component need to be after children, because for useWatch hook order matters.
       * We need to have all fields registered before we start any adjustments using useEffect, otherwise
       * react-hook-form would skip re-rendering of the components.
       */}
      <FormAdjustments isOrderChangeProcess={isOrderChangeProcess} />
    </FormProvider>
  )
}

const FormAdjustments = ({isOrderChangeProcess}: {isOrderChangeProcess: boolean}) => {
  useAdjustMultipleTrucks()
  useLoadSpacingAdjustment(isOrderChangeProcess)
  useLoadBalanceAdjustment()

  return null
}
